/**
 * 10 visual identity のデザイントークン（CSS 変数値）
 */
import type { VisualIdentity } from '../types/index';

export interface IdentityTokens {
  bg: string;
  surface: string;
  surfaceAlt: string;
  text: string;
  textMuted: string;
  border: string;
  accent: string;
  accentSoft: string;
  accentText: string;
  fontHeading: string;
  fontBody: string;
  radiusSm: string;
  radiusMd: string;
  radiusLg: string;
  radiusXl: string;
  shadowSm: string;
  shadowLg: string;
}

export const IDENTITIES: Record<VisualIdentity, IdentityTokens> = {
  light: {
    bg: '#FFFFFF',
    surface: '#FAFAF8',
    surfaceAlt: '#F5F4F1',
    text: '#0A0A0A',
    textMuted: '#6B6B6B',
    border: '#E5E5E5',
    accent: '#0A0A0A',
    accentSoft: '#F0F0F0',
    accentText: '#FFFFFF',
    fontHeading: '"Geist", "Inter", system-ui, sans-serif',
    fontBody: '"Geist", "Inter", system-ui, sans-serif',
    radiusSm: '6px',
    radiusMd: '10px',
    radiusLg: '16px',
    radiusXl: '24px',
    shadowSm: '0 1px 2px rgba(0,0,0,0.04)',
    shadowLg: '0 10px 30px rgba(0,0,0,0.08)',
  },
  dark: {
    bg: '#0B0D10',
    surface: '#13161B',
    surfaceAlt: '#1A1E25',
    text: '#F5F5F5',
    textMuted: '#9CA3AF',
    border: '#262B33',
    accent: '#7CFFB2',
    accentSoft: '#1F2A24',
    accentText: '#0B0D10',
    fontHeading: '"Geist", "Inter", system-ui, sans-serif',
    fontBody: '"Geist", "Inter", system-ui, sans-serif',
    radiusSm: '6px',
    radiusMd: '10px',
    radiusLg: '16px',
    radiusXl: '24px',
    shadowSm: '0 1px 2px rgba(0,0,0,0.3)',
    shadowLg: '0 10px 30px rgba(0,0,0,0.5)',
  },
  vibrant: {
    bg: '#0E0524',
    surface: '#1A0F38',
    surfaceAlt: '#251849',
    text: '#FFFFFF',
    textMuted: '#C4B8E0',
    border: '#3A2766',
    accent: '#FF4D8D',
    accentSoft: '#3A1A2C',
    accentText: '#FFFFFF',
    fontHeading: '"Space Grotesk", "Inter", system-ui, sans-serif',
    fontBody: '"Inter", system-ui, sans-serif',
    radiusSm: '8px',
    radiusMd: '14px',
    radiusLg: '20px',
    radiusXl: '32px',
    shadowSm: '0 1px 4px rgba(255,77,141,0.15)',
    shadowLg: '0 12px 40px rgba(255,77,141,0.25)',
  },
  pastel: {
    bg: '#FAF4EE',
    surface: '#FFFFFF',
    surfaceAlt: '#F2EAE0',
    text: '#3A2A1F',
    textMuted: '#8A7565',
    border: '#E5D8C8',
    accent: '#E07856',
    accentSoft: '#F8E0D5',
    accentText: '#FFFFFF',
    fontHeading: '"DM Serif Display", Georgia, serif',
    fontBody: '"DM Sans", "Inter", system-ui, sans-serif',
    radiusSm: '8px',
    radiusMd: '16px',
    radiusLg: '24px',
    radiusXl: '36px',
    shadowSm: '0 1px 2px rgba(58,42,31,0.05)',
    shadowLg: '0 12px 36px rgba(58,42,31,0.10)',
  },
  editorial: {
    bg: '#F8F5EE',
    surface: '#FFFFFF',
    surfaceAlt: '#EFEAE0',
    text: '#1A1A1A',
    textMuted: '#6B6259',
    border: '#D9D4C8',
    accent: '#1A1A1A',
    accentSoft: '#E8E3D8',
    accentText: '#FFFFFF',
    fontHeading: '"Fraunces", Georgia, serif',
    fontBody: '"Inter", system-ui, sans-serif',
    radiusSm: '2px',
    radiusMd: '4px',
    radiusLg: '6px',
    radiusXl: '8px',
    shadowSm: '0 1px 1px rgba(0,0,0,0.04)',
    shadowLg: '0 8px 24px rgba(0,0,0,0.06)',
  },
  // モノクロ — 純粋な B&W、ブルータリスト/ミニマル
  mono: {
    bg: '#FFFFFF',
    surface: '#FFFFFF',
    surfaceAlt: '#F4F4F4',
    text: '#000000',
    textMuted: '#555555',
    border: '#000000',
    accent: '#000000',
    accentSoft: '#EAEAEA',
    accentText: '#FFFFFF',
    fontHeading: '"Inter", "Helvetica Neue", system-ui, sans-serif',
    fontBody: '"Inter", "Helvetica Neue", system-ui, sans-serif',
    radiusSm: '0px',
    radiusMd: '0px',
    radiusLg: '0px',
    radiusXl: '0px',
    shadowSm: 'none',
    shadowLg: 'none',
  },
  // ミント — 清潔感のあるティール緑、健康・ウェルネス向け
  mint: {
    bg: '#F4FBF8',
    surface: '#FFFFFF',
    surfaceAlt: '#E6F5EE',
    text: '#0E2A22',
    textMuted: '#5A7A6E',
    border: '#CCE7DC',
    accent: '#0FB28A',
    accentSoft: '#D5F2E6',
    accentText: '#FFFFFF',
    fontHeading: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
    fontBody: '"Inter", system-ui, sans-serif',
    radiusSm: '10px',
    radiusMd: '16px',
    radiusLg: '24px',
    radiusXl: '36px',
    shadowSm: '0 1px 2px rgba(14,42,34,0.05)',
    shadowLg: '0 12px 32px rgba(15,178,138,0.15)',
  },
  // アジュール — 信頼感のあるブルー、コーポレート/テック向け
  azure: {
    bg: '#F5F8FC',
    surface: '#FFFFFF',
    surfaceAlt: '#E8EFF7',
    text: '#0B1A2E',
    textMuted: '#5C6C82',
    border: '#D2DCEA',
    accent: '#2563EB',
    accentSoft: '#DCE7FB',
    accentText: '#FFFFFF',
    fontHeading: '"Inter", system-ui, sans-serif',
    fontBody: '"Inter", system-ui, sans-serif',
    radiusSm: '6px',
    radiusMd: '10px',
    radiusLg: '16px',
    radiusXl: '24px',
    shadowSm: '0 1px 3px rgba(11,26,46,0.06)',
    shadowLg: '0 12px 36px rgba(37,99,235,0.18)',
  },
  // テラコッタ — 暖かみのあるブラウン/オレンジ、オーガニック/自然派
  terra: {
    bg: '#FBF6F0',
    surface: '#FFFFFF',
    surfaceAlt: '#F2E7D8',
    text: '#3A2614',
    textMuted: '#856A4F',
    border: '#E4D2BC',
    accent: '#C2552A',
    accentSoft: '#F4DCC7',
    accentText: '#FFFFFF',
    fontHeading: '"DM Serif Display", Georgia, serif',
    fontBody: '"DM Sans", "Inter", system-ui, sans-serif',
    radiusSm: '6px',
    radiusMd: '12px',
    radiusLg: '20px',
    radiusXl: '28px',
    shadowSm: '0 1px 2px rgba(58,38,20,0.05)',
    shadowLg: '0 12px 36px rgba(194,85,42,0.15)',
  },
  // ノワール — ラグジュアリーダーク、ゴールドアクセント
  noir: {
    bg: '#0A0A0A',
    surface: '#141414',
    surfaceAlt: '#1C1C1C',
    text: '#F2EDE0',
    textMuted: '#A39E8E',
    border: '#2A2A2A',
    accent: '#C9A45C',
    accentSoft: '#3A2E18',
    accentText: '#0A0A0A',
    fontHeading: '"Fraunces", "Playfair Display", Georgia, serif',
    fontBody: '"Inter", system-ui, sans-serif',
    radiusSm: '2px',
    radiusMd: '4px',
    radiusLg: '8px',
    radiusXl: '12px',
    shadowSm: '0 1px 2px rgba(0,0,0,0.4)',
    shadowLg: '0 12px 40px rgba(201,164,92,0.20)',
  },
};

/**
 * Brand 設定の上書き値（BrandPanel で入力する4項目）。
 * 値が指定されている項目は identity のデフォルト値を上書きする。
 */
export interface BrandOverrides {
  primaryColor?: string | null;
  accentColor?: string | null;
  headingFont?: string | null;
  bodyFont?: string | null;
}

/** identity を CSSProperties に変換（インラインスタイル用） */
export function identityVars(
  id: VisualIdentity,
  brand?: BrandOverrides | null,
): Record<string, string | undefined> {
  const t = IDENTITIES[id];
  const text = brand?.primaryColor || t.text;
  const accent = brand?.accentColor || t.accent;
  const fontHeading = brand?.headingFont || t.fontHeading;
  const fontBody = brand?.bodyFont || t.fontBody;
  return {
    '--ls-bg': t.bg,
    '--ls-surface': t.surface,
    '--ls-surface-alt': t.surfaceAlt,
    '--ls-text': text,
    '--ls-text-muted': t.textMuted,
    '--ls-border': t.border,
    '--ls-accent': accent,
    // accentColor 上書き時は accent-soft を accent + 透明度で再生成
    '--ls-accent-soft': brand?.accentColor ? `${accent}26` : t.accentSoft,
    '--ls-accent-text': t.accentText,
    '--ls-font-heading': fontHeading,
    '--ls-font-body': fontBody,
    '--ls-radius-sm': t.radiusSm,
    '--ls-radius-md': t.radiusMd,
    '--ls-radius-lg': t.radiusLg,
    '--ls-radius-xl': t.radiusXl,
    '--ls-shadow-sm': t.shadowSm,
    '--ls-shadow-lg': t.shadowLg,
  };
}
